(function () {
  'use strict';

  // Same fallback image the search page uses — default-magnolia.jpg sits in
  // the same /assets folder as this .js file, so we replace this script's
  // own URL's filename with the image filename to construct its path.
  var PLACEHOLDER_IMAGE = (function () {
    var src = (document.currentScript && document.currentScript.src) || '';
    return src.replace(/algolia-article-listing\.js.*$/, 'default-magnolia.jpg');
  })();

  // Fetches { appId, searchApiKey } from cfg.configEndpoint and merges any
  // present values onto cfg, overriding the fallback baked in by Liquid.
  // Cached in sessionStorage per endpoint so pagination/facet clicks on the
  // same page load don't refetch, and so a page-editor "reload section"
  // doesn't hammer the endpoint. Never throws — a failed/misconfigured
  // endpoint just leaves cfg.appId/searchApiKey as whatever Liquid rendered.
  function applyDynamicCredentials(cfg) {
    if (!cfg.configEndpoint) return Promise.resolve();
    var cacheKey = 'algoliaConfigCache:' + cfg.configEndpoint;
    var cached = null;
    try {
      cached = sessionStorage.getItem(cacheKey);
    } catch {
      // sessionStorage unavailable (private mode, etc.) — fall through to fetch every time.
    }
    if (cached) {
      try {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.appId) cfg.appId = parsed.appId;
        if (parsed && parsed.searchApiKey) cfg.searchApiKey = parsed.searchApiKey;
        return Promise.resolve();
      } catch {
        // fall through to fetch if the cached value is corrupt
      }
    }
    return fetch(cfg.configEndpoint, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (creds) {
        if (creds && creds.appId) cfg.appId = creds.appId;
        if (creds && creds.searchApiKey) cfg.searchApiKey = creds.searchApiKey;
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(creds));
        } catch {
          // ignore storage failures — non-critical
        }
      })
      .catch(function () {
        // Failed to fetch credentials — falling back to the appId/searchApiKey baked into the page.
      });
  }

  async function init() {
    var cfg = window.algoliaArticleListingConfig;
    if (!cfg) {
      return;
    }
    if (typeof algoliasearch === 'undefined') {
      return;
    }
    if (typeof instantsearch === 'undefined') {
      return;
    }

    await applyDynamicCredentials(cfg);

    if (!cfg.appId) {
      return;
    }
    if (!cfg.searchApiKey) {
      return;
    }

    var prefix = cfg.containerPrefix || 'algolia-article';
    var searchClient = algoliasearch(cfg.appId, cfg.searchApiKey);

    // Off by default, same `showFacetCounts` toggle as the search and collection pages.
    var SHOW_FACET_COUNTS = !!(window.__ALGOLIA_CONFIG && window.__ALGOLIA_CONFIG.showFacetCounts === true);
    if (!SHOW_FACET_COUNTS) document.body.classList.add('algolia-hide-facet-counts');

    // --- Initial-load skeleton state ---------------------------------------
    // InstantSearch.js renders every widget once synchronously as soon as
    // search.start() runs, BEFORE the first network request has even been
    // sent — that pre-response render call always reports 0 hits (there's
    // no results object yet). Previously nothing distinguished that from a
    // real "0 results" answer, so on every page load the grid would flash
    // "No recipes/articles match your current filters" + a Clear Filters
    // button, and the Sort by dropdown would pop in, both before the real
    // Algolia response (and its actual result count) ever arrived.
    //
    // hasReceivedResults flips true exactly once, the first time any
    // render callback below sees a real `results` object (i.e. an actual
    // query has resolved — regardless of whether it came back with 0 or
    // 50 hits). Until then, a body-level class drives a skeleton grid
    // (articleSkeletonHtml() below) in place of real/empty hit markup and
    // hides the Sort by control + selected-filters row via CSS — the same
    // loading treatment used on the /search results page. Scoped to
    // <body> (not a local container) so it can hide UI in multiple
    // separate mount points (the drawer's sort row AND the desktop sort
    // anchor) with one flag, but the selectors themselves are scoped to
    // this page's own #recipe-listing-* ids in algolia-article-listing.css
    // so it can never bleed into other page chrome.
    var hasReceivedResults = false;
    document.body.classList.add('algolia-listing-loading');
    function markResultsReceived() {
      if (hasReceivedResults) return;
      hasReceivedResults = true;
      document.body.classList.remove('algolia-listing-loading');
      // Explicit "done loading" signal for CSS, added alongside the
      // loading-class removal above. algolia-article-listing.css used to
      // key the skeleton's hidden state off body:not(.algolia-listing-loading)
      // — which matches at first paint too, before this script has even
      // run, since the body has neither class yet at that point. That
      // hid the server-rendered skeleton (#recipe-listing-skeleton) and
      // its shimmer before it ever had a chance to show. Hiding it now
      // requires this positive class instead, so the skeleton is only
      // ever hidden once we *know* a real response has actually arrived.
      document.body.classList.add('algolia-listing-loaded');
    }

    // Holds the live #<prefix>-sort DOM node once the Sort section below
    // mounts it. Declared up here (not there) so renderArticleHits can
    // close over it — by the time the first render actually fires (after
    // search.start(), at the bottom of this function), the Sort section
    // has already run and assigned it, regardless of where it's declared.
    var sortEl = null;
    // Comment node marking sortEl's liquid-authored resting spot — inside
    // the mobile drawer's "Sort by:" row (see main-blog.liquid). Assigned
    // once, right after sortEl itself, in the Sort section below.
    var sortOriginalMarker = null;

    // On mobile the sort control lives inside the Filter & Sort drawer,
    // which has its own "APPLY" button — so picking a new sort option
    // there must NOT refine immediately (that would re-sort the grid
    // behind the still-open drawer). Instead the choice is held here
    // until the drawer's [data-drawer-apply] button is clicked. On
    // tablet/desktop the control isn't inside a drawer, so it keeps
    // applying immediately on change, same as before.
    //
    // pendingSortIndexName: the replica index name the user picked on
    // mobile but hasn't applied yet (null when nothing is pending).
    // sortConfirmedIndexName: the replica index name actually applied to
    // the search (mirrors currentRefinement), used to snap the <select>
    // back if the drawer is reopened/dismissed without hitting Apply.
    // sortRefineFn: latest refine() from connectSortBy's render, called
    // by the drawer's Apply handler below.
    var pendingSortIndexName = null;
    var sortConfirmedIndexName = null;
    var sortRefineFn = null;

    // Mobile drawer panel state: 'main' shows list of facets, 'facet' shows checkboxes for a single facet
    //var drawerPanel = 'main'; // 'main' | 'facet'
    var drawerFacetAttr = null; // Current facet attribute when in 'facet' panel

    // Pending facet refinements: { attr: [value1, value2, ...] }
    // Stored when checkboxes are toggled, applied when APPLY button is clicked
    var pendingRefinements = {};
    //var refinementWidgets = {}; // Store widget refine functions: { attr: refineFn }

    // Mobile/tablet+ boundary matches the 750px breakpoint already used
    // everywhere else in algolia-article-listing.css.
    function isMobileFilterTier() {
      return !(window.matchMedia && window.matchMedia('(min-width: 750px)').matches);
    }

    // Sort widget's home differs by viewport tier: on mobile it lives
    // inside the Filter & Sort drawer's own "Sort by:" row (its
    // liquid-authored position); on tablet/desktop it lives in the
    // dedicated #recipe-listing-sort-anchor mount above the results grid
    // (see main-blog.liquid), next to the results count — NOT inside the
    // filters sidebar, matching the reference screenshot. Physically moves
    // the existing node either way — never recreates the widget — so its
    // listeners/current selection survive every move.
    function positionSortWidget() {
      if (!sortEl) return;
      if (isMobileFilterTier()) {
        if (sortOriginalMarker && sortOriginalMarker.parentNode) {
          sortOriginalMarker.parentNode.insertBefore(sortEl, sortOriginalMarker.nextSibling);
        }
      } else {
        var anchor = document.getElementById(prefix + '-sort-anchor');
        if (anchor) anchor.appendChild(sortEl);
      }
    }

    var search = instantsearch({
      indexName: cfg.sortReplicas ? cfg.sortReplicas.relevance : cfg.indexName,
      searchClient: searchClient,
      // Sync filters/sort/page into the URL so state is shareable/bookmarkable
      // and the back button steps through previous states (AC: URL sync, back button).
      routing: {
        router: instantsearch.routers.history({
          cleanUrlOnDispose: false,
        }),
        stateMapping: instantsearch.stateMappings.simple(),
      },
      future: { preserveSharedStateOnUnmount: true },
    });

    // --- Scope this page to its content type (blog vs recipe) ------------
    search.addWidgets([
      instantsearch.widgets.configure({
        filters: cfg.articleTypeAttribute + ':' + cfg.articleType,
        hitsPerPage: cfg.hitsPerPage || 12,
      }),
    ]);

    // --- Results grid ------------------------------------------------------
    // Self-contained card markup — every class below (article-hit*) is
    // owned and styled entirely by algolia-article-listing.css, not by
    // the theme's component-article-card.css / component-card.css. This
    // is deliberate: it means this listing's look doesn't silently break
    // if the theme's card component changes shape later.
    //
    // Built on connectHits (not the instantsearch.widgets.hits widget)
    // specifically so the list wrapper and each item wrapper render as
    // <div>s instead of the <ol>/<li> the built-in hits widget always
    // produces — cssClasses.list/.item only let you rename classes on
    // that ol/li, not change the tag itself, so a custom connector is
    // required to actually change the tag names. Class names
    // (blog-articles / blog-articles__hit-wrapper) are unchanged, so the
    // grid CSS and loading-state selector both keep working as-is.
    function withWidth(src, width) {
      if (!src) return src;
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    // Handles whatever shape the index actually stores the publish date
    // in — a preformatted string ("May 13, 2026"), an ISO 8601 string, or
    // a Unix timestamp in seconds or milliseconds — and always outputs
    // "Month D, YYYY" to match the rest of the site.
    function formatDate(value) {
      if (!value && value !== 0) return '';
      var d;
      if (typeof value === 'number') {
        // Algolia/Shopify timestamps are commonly stored in seconds;
        // anything shorter than a millisecond-epoch is treated as seconds.
        d = new Date(value < 1e12 ? value * 1000 : value);
      } else {
        d = new Date(value);
      }
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    function articleItemHtml(hit) {
      var image = hit.image || hit.featuredImage || '';
      var url = hit.url || (hit.handle ? '/blogs/' + cfg.articleType + '/' + hit.handle : '#');
      var rawDate =
        hit.publishedAt || hit.published_at || hit.publishDate || hit.publish_date || hit.date || hit.createdAt || '';
      var date = formatDate(rawDate);
      var title = hit.title || '';
      var animateAttrs = window.__algoliaAnimationsRevealOnScroll
        ? ' data-cascade style="--animation-order: ' + (hit.__position || 1) + ';"'
        : '';

      // If no image exists, use the placeholder (matches search page behavior)
      var finalImage = image || PLACEHOLDER_IMAGE;

      // Shopify CDN images accept a `width` query param for on-the-fly
      // resizing, so we can build a real srcset from the single
      // hit.image URL instead of shipping one full-size image to every
      // viewport. For the placeholder image (not from Shopify CDN), skip
      // the srcset and just use the src directly.
      var widths = [360, 533, 720, 1000, 1500];
      var srcset = image
        ? widths
            .map(function (w) {
              return withWidth(image, w) + ' ' + w + 'w';
            })
            .join(',')
        : '';
      var sizesAttr = '(min-width: 1023px) 33vw, (min-width: 749px) 50vw, 100vw';

      var articleHtml =
        '<div class="blog-articles__article article' +
        (window.__algoliaAnimationsRevealOnScroll ? ' scroll-trigger animate--slide-in' : '') +
        '"' +
        animateAttrs +
        ' aria-label="Article: ' +
        title +
        '">' +
        '<a class="article-hit" href="' +
        url +
        '">' +
        '<div class="article-hit__image-wrap">' +
        '<img class="article-hit__image"' +
        (srcset ? ' srcset="' + srcset + '"' : '') +
        ' src="' +
        (image ? withWidth(image, 533) : finalImage) +
        '"' +
        (srcset ? ' sizes="' + sizesAttr + '"' : '') +
        ' alt="' +
        title +
        '" loading="lazy">' +
        '</div>' +
        '<div class="article-hit__body">' +
        '<h3 class="article-hit__title">' +
        title +
        '</h3>' +
        (date ? '<time class="article-hit__date">' + date + '</time>' : '') +
        '</div>' +
        '</a>' +
        '</div>';

      // Same "item" wrapper div the built-in widget's li carried
      // (blog-articles__hit-wrapper), just as a div instead of an li.
      return '<div class="blog-articles__hit-wrapper">' + articleHtml + '</div>';
    }

    function articleEmptyHtml() {
      // Use contextual label: "recipes" on the recipe blog, "blogs" on the article blog.
      var itemLabel = cfg.articleType === 'recipe' ? 'recipes' : 'blogs';
      return (
        '<div class="article-listing__empty">' +
        '<h2 class="article-listing__empty-text">' +
        'Sorry, no ' +
        itemLabel +
        ' match your criteria.' +
        '<br class="article-listing__empty-break">' +
        'Clear or modify your filters and try again.' +
        '</h2>' +
        '<button type="button" class="article-listing__clear-cta button button--secondary" data-clear-refinements-trigger>CLEAR FILTERS</button>' +
        '</div>'
      );
    }

    // Placeholder grid shown only before the first real Algolia response
    // has arrived (see hasReceivedResults above) — never a substitute for
    // an actual empty state. Card count matches hitsPerPage so the
    // skeleton grid is the same shape as the real one that replaces it,
    // avoiding a layout jump. Reuses the real card's own box-sizing
    // classes (article-hit__image-wrap, article-hit__body) so the
    // skeleton doesn't need its own separate dimensions to keep in sync
    // with — only a couple of small "--skeleton" modifier classes (see
    // algolia-article-listing.css) swap in the flat placeholder color and
    // hide real content that isn't there yet (no href, no image, no
    // text).
    function articleSkeletonHtml() {
      var count = cfg.hitsPerPage || 12;
      var cardHtml =
        '<div class="blog-articles__hit-wrapper">' +
        '<div class="article-hit article-hit--skeleton" aria-hidden="true">' +
        '<div class="article-hit__image-wrap article-hit__image-wrap--skeleton"></div>' +
        '<div class="article-hit__body">' +
        '<div class="article-hit__skeleton-line article-hit__skeleton-line--title"></div>' +
        '<div class="article-hit__skeleton-line article-hit__skeleton-line--date"></div>' +
        '</div></div></div>';
      var cardsHtml = '';
      for (var i = 0; i < count; i++) {
        cardsHtml += cardHtml;
      }
      var gridClass = cfg.gridClass || 'blog-articles';
      // Add --no-facets class if no facets were registered
      var gridClassWithModifier = gridClass + (hasFacetsRegistered ? '' : ' blog-articles--no-facets');
      return '<div class="' + gridClassWithModifier + ' is-skeleton">' + cardsHtml + '</div>';
    }

    var renderArticleHits = function (renderOptions) {
      var container = renderOptions.widgetParams.container;

      if (!renderOptions.results) {
        // Pre-response render (see hasReceivedResults above) — show the
        // loading skeleton instead of treating 0 hits as a real answer.
        container.innerHTML = '<div class="article-hits">' + articleSkeletonHtml() + '</div>';
        return;
      }
      markResultsReceived();
      applyFacetConfig(renderOptions.results);

      var hits = renderOptions.hits;
      var gridClass = cfg.gridClass || 'blog-articles';
      // Add --no-facets class if no facets were registered
      var gridClassWithModifier = gridClass + (hasFacetsRegistered ? '' : ' blog-articles--no-facets');
      var bodyHtml = hits.length
        ? '<div class="' + gridClassWithModifier + '">' + hits.map(articleItemHtml).join('') + '</div>'
        : articleEmptyHtml();
      // "article-hits" is this custom widget's own wrapper div — plays
      // the same role the built-in widget's ".ais-Hits" div did (the CSS
      // targets it the same way).
      container.innerHTML = '<div class="article-hits">' + bodyHtml + '</div>';
    };

    var customHits = instantsearch.connectors.connectHits(renderArticleHits);

    search.addWidgets([
      customHits({
        container: document.getElementById(prefix + '-hits'),
      }),
    ]);

    // --- Loading state (skeleton) ----------------------------------------
    // InstantSearch doesn't expose a native "loading" widget; toggle a class
    // on the grid based on the searchState via search.on('render' / 'search').
    var hitsContainer = document.getElementById(prefix + '-hits');
    search.on('search', function () {
      if (hitsContainer) hitsContainer.classList.add('is-loading');
    });
    search.on('render', function () {
      if (hitsContainer) hitsContainer.classList.remove('is-loading');
    });

    // --- Stats / result count --------------------------------------------
    // Matches the native template's "{{ blog.articles_count }} Items" heading.
    //
    // Built on connectStats (not the built-in stats widget) because the
    // mobile drawer needs the same count in two places at once — the root
    // screen's header ("FILTER AND SORT ... 43 Items") and the drilled-in
    // facet screen's header ("< TAGS ... 43 Items") — and the built-in
    // widget can only ever target one container. All elements sharing the
    // .js-drawer-count class (see main-blog.liquid) get updated together
    // on every render.
    function renderStats(renderOptions) {
      var nbHits = renderOptions.nbHits;
      var text = nbHits + (nbHits === 1 ? ' Item' : ' Items');
      var countEls = document.querySelectorAll('.js-drawer-count');
      for (var i = 0; i < countEls.length; i++) {
        countEls[i].textContent = text;
      }
    }
    var customStats = instantsearch.connectors.connectStats(renderStats);
    search.addWidgets([customStats({})]);

    // --- Selected filter chips --------------------------------------------
    // Reuses the native #selected-tags-display / .tag-chip markup so no new
    // CSS is needed here.
    //
    // Built on connectCurrentRefinements (not the instantsearch.widgets.
    // currentRefinements widget) so the chip markup AND the container's
    // display toggle happen in one single render callback. The previous
    // version used the built-in widget to render the chips, then a
    // *separate* `search.on("render", ...)` listener that queried the DOM
    // afterward for ".tag-chip" to decide visibility — two independent
    // listeners both reacting to the same "render" event, rather than one
    // render pass. That left the container able to end up re-hidden
    // (inline `display: none`) even after a filter was selected. Deciding
    // visibility from `items.length` inside the same callback that writes
    // the chips removes that gap entirely — there's no longer a second
    // pass that can read stale DOM.
    function renderSelectedTags(renderOptions) {
      var items = renderOptions.items;
      var refine = renderOptions.refine;
      var container = renderOptions.widgetParams.container;
      if (!container) return;

      var chipsHtml = items.length
        ? items
            .map(function (item, itemIndex) {
              return item.refinements
                .map(function (refinement, refinementIndex) {
                  return (
                    '<span class="algolia-current-refinement">' +
                    refinement.label +
                    '<button type="button" class="algolia-current-refinement__remove" data-item-index="' +
                    itemIndex +
                    '" data-refinement-index="' +
                    refinementIndex +
                    '" aria-label="Remove filter ' +
                    refinement.label +
                    '"><svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" width="12" height="12"><path d="M2 2L12 12M12 2L2 12"></path></svg></button></span>'
                  );
                })
                .join('');
            })
            .join('')
        : '';

      // Chips get their own wrapper matching search page structure:
      // Clear button FIRST, then chips list
      var clearLinkHtml = items.length
        ? '<button type="button" class="algolia-current-refinements__clear-all">Clear all filters</button>'
        : '';

      container.innerHTML = chipsHtml
        ? '<div class="algolia-current-refinements">' +
          clearLinkHtml +
          '<div class="algolia-current-refinements__list">' +
          chipsHtml +
          '</div></div>'
        : '';
      // Explicitly set "flex", not "" — clearing to "" would just remove
      // the inline override and fall back to the stylesheet's own
      // unconditional `display: none` (see algolia-article-listing.css).
      // Unlike the chips themselves, the sort dropdown appended below
      // always needs to be visible here, whether or not any filters are
      // currently selected — so this container's visibility is no longer
      // conditional on `items.length` the way the chip markup is.
      container.style.display = 'flex';
      // Drives the mobile-only grey background block (see
      // algolia-article-listing.css) — only show that block when there's
      // actually something to display; an empty bar with no chips and no
      // visible sort control (sort lives in the drawer on mobile) would
      // just be dead space.
      container.classList.toggle('has-selected-tags', items.length > 0);

      // innerHTML above wipes any previously-bound listeners, so rewire on
      // every render rather than once (same pattern as renderPagination
      // below). Only relevant when there are actually chips to remove.
      if (items.length) {
        var removeButtons = container.querySelectorAll('.algolia-current-refinement__remove');
        for (var i = 0; i < removeButtons.length; i++) {
          removeButtons[i].addEventListener('click', function (e) {
            var btn = e.currentTarget;
            var item = items[parseInt(btn.getAttribute('data-item-index'), 10)];
            if (!item) return;
            var refinement = item.refinements[parseInt(btn.getAttribute('data-refinement-index'), 10)];
            if (refinement) refine(refinement);
          });
        }

        var clearLinkEl = container.querySelector('.algolia-current-refinements__clear-all');
        if (clearLinkEl) {
          clearLinkEl.addEventListener('click', function (e) {
            e.preventDefault();
            search.helper.clearRefinements().search();
            // Clear pending refinements so checkboxes uncheck when drawer reopens
            pendingRefinements = {};
          });
        }
      }

      // Sort now lives in its own #recipe-listing-sort-anchor mount (see
      // main-blog.liquid) above the grid on tablet/desktop, or the drawer's
      // "Sort by:" row on mobile — neither is inside this chips container,
      // so this innerHTML rewrite never touches it. Still re-run the
      // positioning defensively on every render in case a viewport change
      // and a refinement change land in the same tick.
      positionSortWidget();

      // Also keep the "Clear All Filters" drawer footer button's disabled
      // state in sync with whether there's anything to clear — matches
      // search page behavior (disabled when no filters, enabled when filters active).
      var clearAllButton = document.getElementById('clear-filters-button');
      if (clearAllButton) {
        clearAllButton.disabled = items.length === 0;
      }
    }

    var customCurrentRefinements = instantsearch.connectors.connectCurrentRefinements(renderSelectedTags);
    search.addWidgets([
      customCurrentRefinements({
        container: document.getElementById(prefix + '-selected-tags'),
      }),
    ]);

    // --- Responsive reposition: selected-tags + sort container -----------
    // Three tiers, matching the tablet/desktop breakpoints already used
    // elsewhere in algolia-article-listing.css (749px / 1023px):
    //
    //  - Mobile  (<750px):  stays right where main-blog.liquid put it —
    //    inside .filters-container, alongside .filters-panel, as part of
    //    the filter drawer.
    //  - Tablet  (750–1022px): moves to sit directly below the filters
    //    dropdown/toggle bar — i.e. still inside #blog-list, as a sibling
    //    positioned immediately before #<prefix>-hits (right after
    //    .filters-container).
    //  - Desktop (>=1023px): moves further up, between the page's <h1>
    //    heading and #blog-list itself (the wrapper that holds both
    //    .filters-container and the hits grid) — a sibling of #blog-list,
    //    not merely a sibling of the hits grid nested inside it.
    //
    // Since the sort dropdown lives inside this same container (appended
    // by renderSelectedTags above), moving this one node brings both the
    // chips and the sort control along with it at every tier.
    //
    // This physically reparents the existing node (never recreates it) so
    // its content/listeners survive the move at every tier. A comment
    // node left in its original spot marks exactly where to put it back
    // on mobile.
    var selectedTagsContainer = document.getElementById(prefix + '-selected-tags');
    // #blog-list is a fixed id set directly in main-blog.liquid (not
    // templated per-prefix, same as #blog-data right next to it), so it's
    // looked up by that literal id rather than via `prefix`.
    var blogListEl = document.getElementById('blog-list');
    if (
      selectedTagsContainer &&
      blogListEl &&
      blogListEl.parentNode &&
      hitsContainer &&
      hitsContainer.parentNode &&
      selectedTagsContainer.parentNode
    ) {
      var selectedTagsOriginalMarker = document.createComment(prefix + '-selected-tags-original-position');
      selectedTagsContainer.parentNode.insertBefore(selectedTagsOriginalMarker, selectedTagsContainer);

      var tabletUpMediaQuery = window.matchMedia('(min-width: 750px)');
      var desktopUpMediaQuery = window.matchMedia('(min-width: 1023px)');

      function getSelectedTagsViewportTier() {
        if (desktopUpMediaQuery.matches) return 'desktop';
        if (tabletUpMediaQuery.matches) return 'tablet';
        return 'mobile';
      }

      // Per the reference filter video, the chips + "Clear all filters"
      // link live scoped to the filters sidebar itself — stacked directly
      // above the "TAGS" panel — on BOTH tablet and desktop, not spanning
      // the full page width above the grid. So both non-mobile tiers share
      // one target: the top of the filters drawer/sidebar's body, right
      // before its first facet panel. Only mobile differs, keeping the
      // original liquid-authored spot inside the drawer's root screen.
      function positionSelectedTagsContainer(tier) {
        if (tier === 'mobile') {
          selectedTagsOriginalMarker.parentNode.insertBefore(
            selectedTagsContainer,
            selectedTagsOriginalMarker.nextSibling,
          );
          return;
        }
        var drawerBody = document.querySelector('#recipe-filters-drawer .algolia-drawer__body');
        if (drawerBody) {
          drawerBody.insertBefore(selectedTagsContainer, drawerBody.firstChild);
        }
      }

      positionSelectedTagsContainer(getSelectedTagsViewportTier());
      positionSortWidget();

      function handleSelectedTagsViewportChange() {
        positionSelectedTagsContainer(getSelectedTagsViewportTier());
        positionSortWidget();
      }

      // addEventListener on a MediaQueryList is the modern API; older
      // Safari (<14) only supports the deprecated addListener form.
      if (tabletUpMediaQuery.addEventListener) {
        tabletUpMediaQuery.addEventListener('change', handleSelectedTagsViewportChange);
        desktopUpMediaQuery.addEventListener('change', handleSelectedTagsViewportChange);
      } else {
        tabletUpMediaQuery.addListener(handleSelectedTagsViewportChange);
        desktopUpMediaQuery.addListener(handleSelectedTagsViewportChange);
      }
    }

    // --- Facet panel wrapper ----------------------------------------------
    // Tablet/desktop (per the reference "recipe landing page" filter video)
    // shows each facet as a collapsible "TAGS ⌃" panel — a bold uppercase
    // label with a plain caret toggle, no drawer chrome. The mobile drawer
    // already gets its own equivalent heading (the "< TAGS ... ×" bar built
    // from data-facet-label in main-blog.liquid), so the Panel's own header
    // is hidden there via CSS (.algolia-drawer__facet-list .ais-Panel-header)
    // and only shown at >=750px — same mount point either way, only the
    // header's visibility changes.
    //
    // collapsed always starts `false` (expanded) — matches the video, where
    // every facet panel is open by default.
    //
    // collapseButtonText below swaps InstantSearch's default triangle icon
    // (viewBox 0 0 500 500) for the same chevron used on the search
    // results page's custom .algolia-filter-group__title buttons
    // (algolia-instantsearch.js), so both pages share one visual language
    // for "this section collapses." rotation on collapse is handled by
    // the .is-collapsed class toggle in algolia-article-listing.css,
    // mirroring how that page animates its own chevron.
    function panelChevronSvg(collapsed) {
      return (
        '<svg class="ais-Panel-collapseIcon' +
        (collapsed ? ' is-collapsed' : '') +
        '" viewBox="0 0 10 6" width="10" height="10" aria-hidden="true">' +
        '<path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path>' +
        '</svg>'
      );
    }

    // hidden() below is what makes an emptied-out facet disappear entirely
    // (title included) instead of just showing an empty list under a
    // dangling header. Every facet wrapped by makePanel is a refinementList,
    // whose connector always includes `canRefine` in the render options it
    // hands to the panel (true = at least one refinable value present).
    // When another facet's selection narrows this one down to zero values,
    // canRefine flips to false, InstantSearch sets aria-hidden="true" on the
    // .ais-Panel root, and the existing
    // `body.algolia-listing-loaded .ais-Panel[aria-hidden='true']` rule in
    // algolia-article-listing.css (already used for the initial-load
    // skeleton) takes it from there and hides the whole panel — header and
    // body together. As soon as values come back (canRefine true again) the
    // attribute flips back and the panel — title included — reappears.
    // Guard with `options && 'canRefine' in options` so a future non-facet
    // widget reusing makePanel (which wouldn't report canRefine) doesn't get
    // hidden by accident.
    function makePanel(label) {
      return instantsearch.widgets.panel({
        templates: {
          header: function () {
            return label;
          },
          collapseButtonText: function (options) {
            return panelChevronSvg(options.collapsed);
          },
        },
        collapsed: function () {
          return false;
        },
        hidden: function (options) {
          return !!(options && 'canRefine' in options && !options.canRefine);
        },
      });
    }

    // The real toggle control InstantSearch wires up is the small
    // .ais-Panel-collapseButton — on the search results page the entire
    // label+chevron sits inside one <button data-collapse-facet>, so the
    // whole row is clickable there. Match that here without touching how
    // the panel widget manages its own collapsed state: forward a click
    // anywhere on .ais-Panel-header (except a direct hit on the real
    // button, which would otherwise fire this handler AND its own native
    // click) to that same button.
    document.addEventListener('click', function (e) {
      var header = e.target.closest('.ais-Panel-header');
      if (!header) return;
      if (e.target.closest('.ais-Panel-collapseButton')) return;
      var collapseButton = header.querySelector('.ais-Panel-collapseButton');
      if (collapseButton) collapseButton.click();
    });

    // --- Facet Registration ------------------------------------------------
    // Every configured facet is registered up front; the dashboard config that
    // decides which stay visible arrives with the first search response.
    var hasFacetsRegistered = true;

    function registerFacets() {
      if (cfg.categoryAttribute) {
        search.addWidgets([
          makePanel('Tags')(instantsearch.widgets.refinementList)({
            container: '#' + prefix + '-facet-category',
            attribute: cfg.categoryAttribute,
            searchable: false,
            showMore: true,
            limit: 10,
            showMoreLimit: 20,
          }),
        ]);
      }

      if (cfg.articleType === 'recipe') {
        if (cfg.mealTypeAttribute) {
          search.addWidgets([
            makePanel('Meal Type')(instantsearch.widgets.refinementList)({
              container: '#' + prefix + '-facet-meal-type',
              attribute: cfg.mealTypeAttribute,
              showMore: true,
              limit: 10,
              showMoreLimit: 20,
            }),
          ]);
        }
        if (cfg.ingredientAttribute) {
          search.addWidgets([
            makePanel('Ingredient')(instantsearch.widgets.refinementList)({
              container: '#' + prefix + '-facet-ingredient',
              attribute: cfg.ingredientAttribute,
              searchable: true,
              showMore: true,
              limit: 10,
              showMoreLimit: 20,
            }),
          ]);
        }
      }

      if (cfg.articleType === 'article' && cfg.blogCategoryAttribute) {
        search.addWidgets([
          makePanel('Category')(instantsearch.widgets.refinementList)({
            container: '#' + prefix + '-facet-blog-category',
            attribute: cfg.blogCategoryAttribute,
            searchable: false,
            showMore: true,
            limit: 10,
            showMoreLimit: 20,
          }),
        ]);
      }
    }

    function hideFacet(containerSuffix, attribute) {
      var container = document.querySelector('#' + prefix + containerSuffix);
      if (container) {
        var panel = container.closest('.ais-Panel') || container.parentElement;
        if (panel) panel.style.display = 'none';
      }
      var menuItem = document.querySelector("[data-facet-attribute='" + attribute + "']");
      if (menuItem) menuItem.style.display = 'none';
    }

    var facetConfigApplied = false;

    // Runs once, from the first render that carries real results.
    function applyFacetConfig(results) {
      if (facetConfigApplied) return;
      facetConfigApplied = true;

      var raw = results._rawResults && results._rawResults[0];
      var rc = results.renderingContent || (raw && raw.renderingContent);
      var enabledFacets = [];

      // Get list of enabled facets from dashboard
      if (rc && rc.facetOrdering && rc.facetOrdering.facets && rc.facetOrdering.facets.order) {
        enabledFacets = rc.facetOrdering.facets.order;
      } else {
        console.warn(
          '[algolia-article-listing] ⚠️ No facet configuration found in dashboard - no facets will be registered',
        );
      }

      // Helper function to check if a facet is enabled
      function isFacetEnabled(attribute) {
        var enabled = enabledFacets.indexOf(attribute) !== -1;
        return enabled;
      }

      // --- Reorder facet panels + mobile menu items to match Algolia dashboard order ---
      // Maps each known Algolia attribute to its DOM identifiers so we can
      // move both the desktop sidebar panel wrapper (.algolia-drawer__facet-list)
      // and the mobile menu button ([data-open-facet]) into dashboard order.
      // Any attribute not in the map (e.g. future additions) is silently skipped.
      (function reorderFacetsByDashboard() {
        if (!enabledFacets || !enabledFacets.length) return;

        // attribute → { panelKey, facetKey }
        //   panelKey  = value of data-facet-panel / data-open-facet on the elements
        //   facetKey  = value used in data-facet-attribute on the menu <button>
        var attributeMap = {};
        if (cfg.categoryAttribute) attributeMap[cfg.categoryAttribute] = 'category';
        if (cfg.mealTypeAttribute) attributeMap[cfg.mealTypeAttribute] = 'meal-type';
        if (cfg.ingredientAttribute) attributeMap[cfg.ingredientAttribute] = 'ingredient';
        if (cfg.blogCategoryAttribute) attributeMap[cfg.blogCategoryAttribute] = 'blog-category';

        // --- Desktop sidebar: reorder .algolia-drawer__facet-list wrappers ---
        var drawerBody = document.querySelector('#recipe-filters-drawer .algolia-drawer__body');
        if (drawerBody) {
          // Collect the panels that match dashboard attributes in order
          enabledFacets.forEach(function (attribute) {
            var key = attributeMap[attribute];
            if (!key) return;
            var panel = drawerBody.querySelector('.algolia-drawer__facet-list[data-facet-panel="' + key + '"]');
            if (panel) {
              drawerBody.appendChild(panel); // moves to end → results in dashboard order
            }
          });
        }

        // --- Mobile drawer menu: reorder menu <button> items ---
        var drawerMenu = document.querySelector('#recipe-filters-drawer .algolia-drawer__menu');
        if (drawerMenu) {
          // Sort row ("Sort by:") must stay last — capture it before we move buttons
          var sortRow = drawerMenu.querySelector('.algolia-drawer__sort-row');
          enabledFacets.forEach(function (attribute) {
            var key = attributeMap[attribute];
            if (!key) return;
            var btn = drawerMenu.querySelector('.algolia-drawer__menu-item[data-open-facet="' + key + '"]');
            if (btn) {
              // Insert before the sort row so sort always stays last
              drawerMenu.insertBefore(btn, sortRow || null);
            }
          });
        }
      })();

      // Hide the facets the dashboard has disabled
      var configuredFacets = [[cfg.categoryAttribute, '-facet-category']];
      if (cfg.articleType === 'recipe') {
        configuredFacets.push([cfg.mealTypeAttribute, '-facet-meal-type']);
        configuredFacets.push([cfg.ingredientAttribute, '-facet-ingredient']);
      }
      if (cfg.articleType === 'article') {
        configuredFacets.push([cfg.blogCategoryAttribute, '-facet-blog-category']);
      }

      hasFacetsRegistered = false;
      configuredFacets.forEach(function (pair) {
        if (!pair[0]) return;
        if (isFacetEnabled(pair[0])) hasFacetsRegistered = true;
        else hideFacet(pair[1], pair[0]);
      });

      if (!hasFacetsRegistered) applyNoFacetsLayout();
    }

    registerFacets();
    continueInitialization();

    // Declare the continuation function that contains all remaining initialization
    // Full-width grid and sort-only mobile button when the dashboard enables no facets.
    function applyNoFacetsLayout() {
      // Add --no-facets class to the main wrapper to hide sidebar
      var blogWrapper = document.querySelector('.blog-content-wrapper');
      if (blogWrapper) {
        blogWrapper.classList.add('blog-content-wrapper--no-facets');
      }
      // Note: The .blog-articles--no-facets class is added directly in the renderArticleHits function

      // Mobile UI modifications when no facets exist
      var filterButton = document.querySelector('.algolia-drawer__open');
      var sortOnlyButton = document.querySelector('.mobile-sort-only-button');
      var filtersToggleBar = document.querySelector('.filters-toggle-bar');
      var drawerMenu = document.querySelector('.algolia-drawer__menu');

      if (filterButton && sortOnlyButton && filtersToggleBar) {
        // Hide "Filter and Sort" button completely
        filterButton.style.display = 'none';

        // Show "Sort by" button instead
        sortOnlyButton.style.display = 'flex';

        // Add class to reduce spacing
        filtersToggleBar.classList.add('filters-toggle-bar--sort-only');

        // Hide all facet menu items in the drawer (show only sort)
        if (drawerMenu) {
          var facetButtons = drawerMenu.querySelectorAll('.algolia-drawer__menu-item');
          facetButtons.forEach(function (btn) {
            btn.style.display = 'none';
          });
        }

        // Make sort button open the drawer (only sort section visible inside)
        sortOnlyButton.addEventListener('click', function () {
          filterButton.click(); // Reuse existing drawer open logic
        });
      }
    }

    function continueInitialization() {
      // --- Sort ---------------------------------------------------------------
      // Registered normally, before search.start(), on its own static
      // container (like every other widget) so routing/URL-state stays
      // correct. Design wants the control to visually sit inside the
      // selected-filters area though (last child of
      // #recipe-listing-selected-tags, after any .tag-chip spans) —
      // renderSelectedTags above re-appends the sortEl node there on every
      // render (see the comment in that function for why it has to be
      // every render, not just once). All we do here is capture the
      // reference; we never create a second widget instance for it.
      if (cfg.sortReplicas) {
        var sortItems = [
          { label: 'Most relevant', value: cfg.sortReplicas.relevance },
          { label: 'Date, new to old', value: cfg.sortReplicas.dateNewest },
          { label: 'Date, old to new', value: cfg.sortReplicas.dateOldest },
        ];
        // Alphabetical replicas are optional — only add them if the config
        // supplies replica index names, so sites that haven't created the
        // shopify_articles_title_asc / _desc replicas in Algolia yet don't
        // end up with a sort option that 400s.
        if (cfg.sortReplicas.alphaAsc) {
          sortItems.push({ label: 'Alphabetically, A-Z', value: cfg.sortReplicas.alphaAsc });
        }
        if (cfg.sortReplicas.alphaDesc) {
          sortItems.push({ label: 'Alphabetically, Z-A', value: cfg.sortReplicas.alphaDesc });
        }

        // Built on connectSortBy (not instantsearch.widgets.sortBy)
        // specifically so the change handler can be intercepted — the
        // built-in widget calls refine() itself the instant the <select>
        // fires 'change', with no way to defer that. Markup/classes below
        // reproduce the built-in widget's output 1:1
        // (.ais-SortBy > select.ais-SortBy-select > option.ais-SortBy-option)
        // so none of the existing CSS needs to change.
        function renderSort(renderOptions, isFirstRender) {
          var container = renderOptions.widgetParams.container;
          if (!container) return;

          sortRefineFn = renderOptions.refine;

          if (isFirstRender) {
            container.classList.add('ais-SortBy');

            // Create "Sort by:" label matching search page
            var label = document.createElement('label');
            label.className = 'algolia-sort__label';
            label.textContent = 'Sort by:';
            container.appendChild(label);

            // Create dropdown
            var select = document.createElement('select');
            select.className = 'ais-SortBy-select algolia-sort__select';
            renderOptions.options.forEach(function (item) {
              var optionEl = document.createElement('option');
              optionEl.className = 'ais-SortBy-option';
              optionEl.value = item.value;
              optionEl.textContent = item.label;
              select.appendChild(optionEl);
            });
            container.appendChild(select);

            select.addEventListener('change', function (e) {
              var value = e.target.value;
              if (isMobileFilterTier()) {
                // Hold the choice — applied only when the drawer's APPLY
                // button (data-drawer-apply, wired up below) is clicked.
                pendingSortIndexName = value;
              } else {
                pendingSortIndexName = null;
                sortRefineFn(value);
              }
            });
          }

          var selectEl = container.querySelector('select');
          if (pendingSortIndexName === null) {
            // No unapplied mobile choice waiting — keep the control synced
            // with whatever index is actually live.
            selectEl.value = renderOptions.currentRefinement;
            sortConfirmedIndexName = renderOptions.currentRefinement;
          } else if (pendingSortIndexName === renderOptions.currentRefinement) {
            // The pending choice has now actually been applied (Apply was
            // clicked) — clear it so future renders sync normally again.
            pendingSortIndexName = null;
          }
        }

        var customSortBy = instantsearch.connectors.connectSortBy(renderSort);
        search.addWidgets([
          customSortBy({
            container: document.getElementById(prefix + '-sort'),
            items: sortItems,
          }),
        ]);
        sortEl = document.getElementById(prefix + '-sort');
        if (sortEl && sortEl.parentNode) {
          sortOriginalMarker = document.createComment(prefix + '-sort-original-position');
          sortEl.parentNode.insertBefore(sortOriginalMarker, sortEl);
        }
      }

      // --- Pagination -----------------------------------------------------
      // Built on connectPagination (not instantsearch.widgets.pagination):
      // the built-in widget always renders <a> (or <span> when disabled)
      // with no way to swap the wrapping tag, so a custom connector is
      // required to get real <button> elements.
      //
      // Markup/classes/behavior below are ported 1:1 from the /search
      // results page's own hand-built pagination (see renderPagination /
      // buildPaginationParts in algolia-instantsearch.js) so the two pages
      // look and behave identically: same windowed page numbers with a
      // "…" ellipsis when pages are skipped, same current-page underline
      // treatment, same prev/next arrow buttons that disappear entirely
      // (rather than disable) on the first/last page. algolia-instantsearch.css's
      // .pagination-wrapper/.pagination__list/.pagination__item rules are
      // reused as-is — algolia-article-listing.css only needs to load
      // after (or instead of duplicating) that block.
      //
      // The connector's own `pages` array (built from its `padding` option)
      // isn't used for the window — buildPaginationParts recomputes the
      // same delta-2-around-current window plus first/last page directly
      // from nbPages/currentRefinement, matching the search page exactly.
      // refine() accepts any 0-indexed page directly, so clicking a window
      // page, an edge page, or prev/next all just call refine(page).
      var caretSvg =
        '<span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true">' +
        '<path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path>' +
        '</svg></span>';

      // Same windowing algorithm as buildPaginationParts() in
      // algolia-instantsearch.js: always show page 1 and the last page,
      // a window of up to 2 pages on either side of the current page, and
      // an ellipsis marker wherever there's a gap.
      function buildPaginationParts(current, total) {
        var currentDisplay = current + 1; // 1-indexed
        var delta = 2;
        var parts = [];
        var i;

        parts.push(1);

        var windowStart = Math.max(2, currentDisplay - delta);
        if (windowStart > 2) {
          parts.push('ellipsis');
        }

        for (i = windowStart; i <= Math.min(total, currentDisplay + delta); i++) {
          if (i !== 1 && i !== total) {
            parts.push(i);
          }
        }

        var windowEnd = Math.min(total - 1, currentDisplay + delta);
        if (windowEnd < total - 1) {
          parts.push('ellipsis');
        }

        if (total > 1) {
          parts.push(total);
        }

        return parts.map(function (p) {
          if (p === 'ellipsis') {
            return { type: 'ellipsis' };
          }
          return {
            type: p === currentDisplay ? 'current' : 'page',
            display: p,
            algoliaPage: p - 1,
          };
        });
      }

      function renderPagination(renderOptions) {
        var currentRefinement = renderOptions.currentRefinement;
        var isFirstPage = renderOptions.isFirstPage;
        var isLastPage = renderOptions.isLastPage;
        var nbPages = renderOptions.nbPages;
        var refine = renderOptions.refine;
        var container = renderOptions.widgetParams.container;

        if (!container) return;

        if (nbPages <= 1) {
          container.innerHTML = '';
          return;
        }

        var parts = buildPaginationParts(currentRefinement, nbPages);
        var itemsHtml = '';

        // Previous — omitted entirely on page 1, same as the search page
        // (rather than rendered disabled).
        if (!isFirstPage) {
          itemsHtml +=
            '<li><button type="button" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-page="' +
            (currentRefinement - 1) +
            '" aria-label="Previous page">' +
            caretSvg +
            '</button></li>';
        }

        parts.forEach(function (part) {
          if (part.type === 'ellipsis') {
            itemsHtml += '<li><span class="pagination__item">&hellip;</span></li>';
          } else if (part.type === 'current') {
            itemsHtml +=
              '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ' +
              part.display +
              '">' +
              part.display +
              '</a></li>';
          } else {
            itemsHtml +=
              '<li><button type="button" class="pagination__item link" data-page="' +
              part.algoliaPage +
              '" aria-label="Page ' +
              part.display +
              '">' +
              part.display +
              '</button></li>';
          }
        });

        // Next — omitted entirely on the last page.
        if (!isLastPage) {
          itemsHtml +=
            '<li><button type="button" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-page="' +
            (currentRefinement + 1) +
            '" aria-label="Next page">' +
            caretSvg +
            '</button></li>';
        }

        container.innerHTML =
          '<div class="pagination-wrapper"><nav class="pagination" role="navigation" aria-label="Pagination">' +
          '<ul class="pagination__list list-unstyled" role="list">' +
          itemsHtml +
          '</ul></nav></div>';

        // innerHTML above wipes any previously-bound listeners, so rewire
        // on every render rather than once.
        var buttons = container.querySelectorAll('.pagination__item[data-page]');
        for (var i = 0; i < buttons.length; i++) {
          buttons[i].addEventListener('click', function (e) {
            refine(parseInt(e.currentTarget.getAttribute('data-page'), 10));
            // Matches the search page's own paginate-and-scroll behavior.
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
        }
      }

      var customPagination = instantsearch.connectors.connectPagination(renderPagination);
      search.addWidgets([
        customPagination({
          container: document.getElementById(prefix + '-pagination'),
        }),
      ]);

      // --- Clear all filters ------------------------------------------------
      // No separate widget-rendered button here — we reuse the existing
      // #clear-filters-button already in main-blog.liquid's filters-panel
      // markup so the native styling/position is untouched.
      var clearButton = document.getElementById('clear-filters-button');
      if (clearButton) {
        clearButton.addEventListener('click', function (e) {
          e.preventDefault();

          // Clear all filters and pending refinements
          search.helper.clearRefinements().search();
          pendingRefinements = {};

          // Close drawer immediately and reload results (matches search page behavior)
          closeMobileFilterDrawer();
        });
      }

      // --- Mobile drawer navigation (Tags / Meal Type / Ingredient drill-in) --
      // Two screens live in #recipe-filters-drawer at all times (see
      // main-blog.liquid): [data-drawer-view="root"] shows the top-level
      // menu (Tags / Meal Type / Ingredient / Sort by); [data-drawer-view=
      // "facet"] shows a single drilled-into facet's checkbox list with a
      // back arrow. The underlying refinementList widgets stay mounted the
      // whole time regardless of which screen is showing — this block only
      // ever toggles visibility (via the is-active-facet class and the
      // data-drawer-view attribute itself), never re-creates or re-mounts
      // anything Algolia-related. Entirely independent of search state, so
      // it doesn't need to wait for search.start() below.
      var drawerPanel = document.getElementById('recipe-filters-drawer');
      if (drawerPanel) {
        var facetPanels = drawerPanel.querySelectorAll('[data-facet-panel]');
        var facetTitleEl = drawerPanel.querySelector('[data-facet-panel-title]');

        function openDrawerFacet(facetKey, label) {
          for (var i = 0; i < facetPanels.length; i++) {
            facetPanels[i].classList.toggle(
              'is-active-facet',
              facetPanels[i].getAttribute('data-facet-panel') === facetKey,
            );
          }
          if (facetTitleEl) facetTitleEl.textContent = label || '';
          drawerPanel.setAttribute('data-drawer-view', 'facet');
        }

        function closeDrawerFacet() {
          drawerPanel.setAttribute('data-drawer-view', 'root');
        }

        drawerPanel.addEventListener('click', function (e) {
          var openTrigger = e.target.closest('[data-open-facet]');
          if (openTrigger) {
            openDrawerFacet(openTrigger.getAttribute('data-open-facet'), openTrigger.getAttribute('data-facet-label'));
            return;
          }
          if (e.target.closest('[data-drawer-back]')) {
            closeDrawerFacet();
            return;
          }
          if (e.target.closest('[data-drawer-apply]')) {
            // Filtering is already live via Algolia. Sort is deferred on
            // mobile (see the Sort section above), so commit any pending
            // sort choice now, before closing the drawer.
            if (pendingSortIndexName !== null && sortRefineFn) {
              sortRefineFn(pendingSortIndexName);
            }
            // Apply just needs to close the drawer. Reuses the theme's own
            // "Close ×" control (whatever filter-panel.js already wires up
            // to actually hide .filters-panel) rather than guessing at that
            // mechanism here, same approach as handleShowResultsActivation
            // below.
            var closeButton = drawerPanel.querySelector('.filters-close');
            if (closeButton) closeButton.click();
          }
        });

        // Always land back on the root screen when the drawer is (re)opened,
        // so a previous drill-in doesn't linger next time it's opened. Also
        // discard any sort choice picked-but-never-applied from a prior
        // visit to the drawer, so the dropdown reopens showing whatever
        // sort is actually live rather than a stale unapplied pick.
        function resetDrawerOnOpen() {
          closeDrawerFacet();
          if (pendingSortIndexName !== null) {
            pendingSortIndexName = null;
            if (sortEl) {
              var selectEl = sortEl.querySelector('select');
              if (selectEl && sortConfirmedIndexName !== null) {
                selectEl.value = sortConfirmedIndexName;
              }
            }
          }
        }
        var drawerOpenButtons = document.querySelectorAll('.algolia-drawer__open');
        for (var k = 0; k < drawerOpenButtons.length; k++) {
          drawerOpenButtons[k].addEventListener('click', resetDrawerOnOpen);
        }
      }

      // The native "SHOW RESULTS" button triggers a page reload to
      // /blogs/<type>/tagged/<tag> — Shopify's built-in blog tag URL, which
      // is the legacy pre-Algolia filtering fallback. filter-panel.js (loaded
      // in main-blog.liquid *before* this file) is the script that owns the
      // filters-panel open/close behavior and very likely still has its own
      // click handler on this same button building and navigating to that
      // tag URL. Filtering is instant via Algolia now, so this button should
      // just close the mobile filters drawer instead.
      //
      // Listening for the form's 'submit' event is NOT enough to stop this:
      // if filter-panel.js's click handler calls preventDefault() on the
      // button's click first, the browser never gets to the point of firing
      // 'submit' at all, so a 'submit' listener here would never run.
      // Instead, bind a CAPTURING click listener on `document`. Capture-phase
      // listeners on an ancestor always fire before any listener bound
      // directly to the button — regardless of which script registered
      // first — so this reliably runs ahead of filter-panel.js's own
      // handler. preventDefault() here cancels the submit button's default
      // activation (which includes the form submission itself), and
      // stopPropagation() keeps filter-panel.js's own click handler on the
      // button from running at all.
      function handleShowResultsActivation(e) {
        var button = e.target.closest('#filter-form .filters-apply-button, #filter-form button[type=submit]');
        if (!button) return;
        e.preventDefault();
        e.stopPropagation();

        // Close the drawer by reusing the theme's own "Close ×" control
        // instead of guessing which class/attribute filter-panel.js toggles
        // to show/hide .filters-panel. That control (.filters-close, see
        // main-blog.liquid) exists specifically to close this same panel, so
        // filter-panel.js necessarily already wires it up correctly —
        // dispatching a real click on it reproduces whatever that mechanism
        // is (class toggle, aria-hidden, inline style, etc.) exactly,
        // without this file needing to know or duplicate it.
        var closeButton = document.querySelector('.filters-close');
        if (closeButton) {
          closeButton.click();
        } else {
          // Fallback only if that control isn't present for some reason.
          var panel = button.closest('.filters-panel') || document.querySelector('.filters-panel');
          if (panel) panel.classList.remove('active', 'visible', 'is-active', 'open');
        }
      }
      document.addEventListener('click', handleShowResultsActivation, true);

      // Fallback for non-click activation (e.g. pressing Enter while
      // focused in the form), which fires 'submit' directly without an
      // intervening 'click' on the button.
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('#filter-form');
        if (!form) return;
        e.preventDefault();
        var closeButton = document.querySelector('.filters-close');
        if (closeButton) {
          closeButton.click();
        } else {
          var panel = form.closest('.filters-panel') || document.querySelector('.filters-panel');
          if (panel) panel.classList.remove('active', 'visible', 'is-active', 'open');
        }
      });

      // One-time debug dump of the raw record shape so you can confirm the
      // attribute names below (image/url/handle/categoryTag/articleType/publishedAt)
      // actually match your shopify_articles index. Remove once confirmed.
      var loggedSample = false;
      search.on('render', function () {
        if (loggedSample) return;
        var results = search.helper && search.helper.lastResults;
        if (results && results.hits && results.hits.length > 0) {
          loggedSample = true;
        } else if (results) {
          loggedSample = true;
          console.warn(
            "[algolia-article-listing] Query for '" +
              prefix +
              "' returned 0 hits. Check: (1) filters '" +
              cfg.articleTypeAttribute +
              ':' +
              cfg.articleType +
              "' matches real records, (2) index name/replica names, (3) API key has search permissions on this index.",
          );
        }
      });

      // Safety net: if the request itself errors out (bad API key, network
      // failure, undeclared facet attribute causing a 400, etc.), connectHits
      // never receives a `results` object and the skeleton from
      // articleSkeletonHtml() would otherwise stay on screen forever. Surface
      // a real error state instead of hanging indefinitely.
      search.on('error', function () {
        if (hasReceivedResults) return;
        markResultsReceived();
        var hitsEl = document.getElementById(prefix + '-hits');
        if (hitsEl) {
          hitsEl.innerHTML =
            '<div class="article-hits"><div class="article-listing__empty">' +
            '<p>Something went wrong loading results. Please try refreshing the page.</p>' +
            '</div></div>';
        }
      });

      search.start();

      // ========================================================================
      // MOBILE FILTER BUTTON & DRAWER (matches search page implementation)
      // ========================================================================

      // Filter icon SVG (matches search page)
      var FILTER_ICON_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-filter" viewBox="0 0 20 20"><path fill="currentColor" fill-rule="evenodd" d="M4.833 6.5a1.667 1.667 0 1 1 3.334 0 1.667 1.667 0 0 1-3.334 0M4.05 7H2.5a.5.5 0 0 1 0-1h1.55a2.5 2.5 0 0 1 4.9 0h8.55a.5.5 0 0 1 0 1H8.95a2.5 2.5 0 0 1-4.9 0m11.117 6.5a1.667 1.667 0 1 0-3.334 0 1.667 1.667 0 0 0 3.334 0M13.5 11a2.5 2.5 0 0 1 2.45 2h1.55a.5.5 0 0 1 0 1h-1.55a2.5 2.5 0 0 1-4.9 0H2.5a.5.5 0 0 1 0-1h8.55a2.5 2.5 0 0 1 2.45-2"></path></svg>';

      // Build mobile filter button HTML (matches search page buildMobileFilterButtonHtml)
      function buildMobileFilterButtonHtml() {
        var stats = search.helper && search.helper.lastResults ? search.helper.lastResults.nbHits : 0;
        return (
          '<button type="button" class="algolia-mobile-filter-button' +
          (hasFacetsRegistered ? '' : ' algolia-mobile-filter-button--sort-only') +
          '">' +
          '<span class="algolia-mobile-filter-button__left">' +
          FILTER_ICON_SVG +
          '<span>' +
          (hasFacetsRegistered ? 'FILTER AND SORT' : 'SORT BY') +
          '</span>' +
          '</span>' +
          '<span class="algolia-mobile-filter-button__count">' +
          stats +
          ' Items' +
          '</span>' +
          '</button>'
        );
      }

      // Render mobile filter button
      function renderMobileFilterButton() {
        var container = document.getElementById('recipe-mobile-filter-btn');
        if (!container) return;
        container.innerHTML = buildMobileFilterButtonHtml();
      }

      // Initial render of filter button
      renderMobileFilterButton();

      // Re-render button when results change (to update count)
      search.on('render', function () {
        renderMobileFilterButton();
      });

      // Render main panel: list of facet categories with arrows
      function renderDrawerMainPanel() {
        var arrowSvg =
          '<svg class="algolia-drawer__nav-arrow" viewBox="0 0 8 14" width="8" height="14" aria-hidden="true"><path d="M1 1l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        var html = '<div class="algolia-drawer__nav">';

        // Get facet menu items from desktop sidebar
        var facetButtons = document.querySelectorAll('.algolia-drawer__menu-item');
        facetButtons.forEach(function (btn) {
          if (btn.style.display !== 'none') {
            var facetAttr = btn.getAttribute('data-open-facet');
            var facetLabel = btn.textContent.trim();

            // Get the actual attribute name to check pending refinements
            var actualFacetAttr = btn.getAttribute('data-facet-attribute');
            var selectedCount = 0;
            if (actualFacetAttr && pendingRefinements[actualFacetAttr]) {
              selectedCount = pendingRefinements[actualFacetAttr].length;
            }

            html += '<button type="button" class="algolia-drawer__nav-row" data-open-facet="' + facetAttr + '">';
            html += '<span>' + facetLabel + '</span>';

            // Add count badge if filters are selected
            if (selectedCount > 0) {
              html += '<span class="algolia-drawer__nav-count">' + selectedCount + '</span>';
            }

            html += arrowSvg;
            html += '</button>';
          }
        });
        html += '</div>';
        return html;
      }

      // Render facet panel: checkboxes for a single facet
      function renderDrawerFacetPanel(facetAttr) {
        try {
          // Get the actual facet attribute name from the menu button
          var menuBtn = document.querySelector('[data-open-facet="' + facetAttr + '"]');

          if (!menuBtn) {
            return '<div style="padding: 20px; text-align: center; color: #999;">Configuration error</div>';
          }

          var actualFacetAttr = menuBtn.getAttribute('data-facet-attribute');

          if (!actualFacetAttr) {
            return '<div style="padding: 20px; text-align: center; color: #999;">Configuration error</div>';
          }

          // Get facet values from InstantSearch helper
          if (!search || !search.helper) {
            return '<div style="padding: 20px; text-align: center; color: #999;">Search not initialized</div>';
          }

          if (!search.helper.lastResults) {
            return '<div style="padding: 20px; text-align: center; color: #999;">Loading filters...</div>';
          }

          var facets = search.helper.lastResults.getFacetValues(actualFacetAttr, { sortBy: ['name:asc'] });

          if (!facets || facets.length === 0) {
            return (
              '<div style="padding: 20px; text-align: center; color: #999;">No filters available for ' +
              actualFacetAttr +
              '</div>'
            );
          }

          // Build checkbox HTML from facet data
          var html =
            '<ul class="algolia-filter-list algolia-drawer__facet-list" style="padding: 20px; list-style: none; margin: 0;">';

          facets.forEach(function (facet) {
            var displayName = facet.name;
            var count = facet.count;
            var value = facet.escapedValue || facet.name;

            // Check pending refinements instead of facet.isRefined
            var isChecked = false;
            if (pendingRefinements[actualFacetAttr]) {
              isChecked = pendingRefinements[actualFacetAttr].indexOf(value) > -1;
            }

            html += '<li class="algolia-filter-item" style="margin-bottom: 12px;">';
            html +=
              '<label class="algolia-filter-label" style="display: flex; align-items: center; gap: 8px; cursor: pointer;">';
            html += '<input type="checkbox" class="algolia-filter-checkbox" ';
            html += 'data-attr="' + actualFacetAttr + '" ';
            html += 'data-value="' + value + '"';
            if (isChecked) html += ' checked';
            html += ' style="width: 18px; height: 18px;">';
            html +=
              '<span style="font-size: 1.4rem; color: #101010;">' +
              displayName +
              (SHOW_FACET_COUNTS ? ' (' + count + ')' : '') +
              '</span>';
            html += '</label>';
            html += '</li>';
          });

          html += '</ul>';

          return html;
        } catch (error) {
          return (
            '<div style="padding: 20px; text-align: center; color: #999;">Error loading filters: ' +
            error.message +
            '</div>'
          );
        }
      }

      // Open mobile filter drawer - resets to main panel
      function openMobileFilterDrawer() {
        drawerPanel = 'main';
        drawerFacetAttr = null;

        // Initialize pending refinements from current search state
        pendingRefinements = {};
        if (search && search.helper && search.helper.state) {
          var state = search.helper.state;

          // Get disjunctive facet refinements (OR behavior - standard for RefinementList)
          var disjunctiveRefinements = state.disjunctiveFacetsRefinements || {};
          Object.keys(disjunctiveRefinements).forEach(function (attr) {
            if (disjunctiveRefinements[attr] && disjunctiveRefinements[attr].length > 0) {
              pendingRefinements[attr] = disjunctiveRefinements[attr].slice(); // clone array
            }
          });
        }

        // Render drawer body first to create the Clear button
        renderDrawerBody();

        // Update Clear All Filters button state based on initialized refinements
        var hasRefinements = Object.keys(pendingRefinements).some(function (key) {
          return pendingRefinements[key] && pendingRefinements[key].length > 0;
        });
        var clearBtn = document.querySelector('#recipe-mobile-filter-drawer .algolia-drawer__clear');
        if (clearBtn) {
          clearBtn.disabled = !hasRefinements;
        }
      }

      // Render drawer body content based on current panel state
      function renderDrawerBody() {
        var drawer = document.getElementById('recipe-mobile-filter-drawer');
        if (!drawer) {
          return;
        }

        var stats = search.helper && search.helper.lastResults ? search.helper.lastResults.nbHits : 0;
        var closeSvg =
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" width="24" height="24" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg>';
        var filterIconSvg =
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-filter" viewBox="0 0 20 20"><path fill="currentColor" fill-rule="evenodd" d="M4.833 6.5a1.667 1.667 0 1 1 3.334 0 1.667 1.667 0 0 1-3.334 0M4.05 7H2.5a.5.5 0 0 1 0-1h1.55a2.5 2.5 0 0 1 4.9 0h8.55a.5.5 0 0 1 0 1H8.95a2.5 2.5 0 0 1-4.9 0m11.117 6.5a1.667 1.667 0 1 0-3.334 0 1.667 1.667 0 0 0 3.334 0M13.5 11a2.5 2.5 0 0 1 2.45 2h1.55a.5.5 0 0 1 0 1h-1.55a2.5 2.5 0 0 1-4.9 0H2.5a.5.5 0 0 1 0-1h8.55a2.5 2.5 0 0 1 2.45-2"></path></svg>';
        var backSvg =
          '<svg viewBox="0 0 8 14" width="8" height="14" aria-hidden="true"><path d="M7 1L1 7l6 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        // Build header based on current panel
        var headerLeft;
        if (drawerPanel === 'facet' && drawerFacetAttr) {
          // Facet panel: show back button + facet name
          var facetBtn = document.querySelector('[data-open-facet="' + drawerFacetAttr + '"]');
          var facetLabel = facetBtn ? facetBtn.textContent.trim() : drawerFacetAttr;
          headerLeft =
            '<button type="button" class="algolia-drawer__back" aria-label="Back">' +
            backSvg +
            '</button>' +
            '<h3 class="algolia-drawer__title">' +
            facetLabel +
            '</h3>';
        } else if (hasFacetsRegistered) {
          // Main panel with facets: show filter icon + "FILTER AND SORT"
          headerLeft =
            '<span class="algolia-drawer__filter-icon">' +
            filterIconSvg +
            '</span>' +
            '<h3 class="algolia-drawer__title">FILTER AND SORT</h3>';
        } else {
          // Sort-only: just "SORT BY"
          headerLeft = '<h3 class="algolia-drawer__title">SORT BY</h3>';
        }

        // CRITICAL: Move sort widget back to original position BEFORE destroying drawer HTML
        // Otherwise the widget children are lost when innerHTML overwrites the mount point
        var oldSortMount = document.getElementById('mobile-drawer-sort-mount');
        var sortWidget = document.getElementById('recipe-listing-sort');
        if (oldSortMount && sortWidget && oldSortMount.hasChildNodes()) {
          while (oldSortMount.firstChild) {
            sortWidget.appendChild(oldSortMount.firstChild);
          }
        }

        var html = '<div class="algolia-drawer-overlay"></div>';
        html += '<div class="algolia-drawer' + (hasFacetsRegistered ? '' : ' algolia-drawer--sort-only') + '">';

        // Header class for drawer
        var headerClass = 'algolia-drawer__header';
        if (drawerPanel === 'facet') {
          headerClass += ' algolia-drawer__header--facet is-loading';
        }

        html += '<div class="' + headerClass + '">';
        html += '<div class="algolia-drawer__header-left">' + headerLeft + '</div>';
        html += '<span class="algolia-drawer__count">' + stats + ' Items</span>';
        html += '<button type="button" class="algolia-drawer__close" aria-label="Close">' + closeSvg + '</button>';
        html += '</div>';

        html += '<div class="algolia-drawer__body">';

        // Render appropriate panel content
        if (hasFacetsRegistered) {
          if (drawerPanel === 'facet') {
            var facetPanelHtml = renderDrawerFacetPanel(drawerFacetAttr);
            html += facetPanelHtml;
          } else {
            var mainPanelHtml = renderDrawerMainPanel();
            html += mainPanelHtml;
          }
        }

        // Add sort dropdown on main panel or when no facets
        if (drawerPanel === 'main' || !hasFacetsRegistered) {
          html += '<div class="algolia-drawer__sort" id="mobile-drawer-sort-container">';
          html += '<div id="mobile-drawer-sort-mount"></div>';
          html += '</div>';
        }

        html += '<div class="algolia-drawer__footer">';
        html += '<button type="button" class="algolia-drawer__apply">APPLY</button>';
        if (hasFacetsRegistered) {
          html += '<button type="button" class="algolia-drawer__clear">CLEAR ALL FILTERS</button>';
        }
        html += '</div>'; // end footer, nested as the last child of .algolia-drawer__body

        html += '</div>'; // end body
        html += '</div>'; // end drawer

        drawer.innerHTML = html;
        drawer.classList.add('is-open');
        document.body.classList.add('no-scroll');

        // Remove loading shimmer from header after content is rendered (show shimmer for at least 600ms)
        setTimeout(function () {
          var header = drawer.querySelector('.algolia-drawer__header.is-loading');
          if (header) {
            header.classList.remove('is-loading');
          }
        }, 600);

        // Move the actual sort widget into the drawer if on main panel
        // This happens AFTER innerHTML is set, so the mount point exists
        if (drawerPanel === 'main' || !hasFacetsRegistered) {
          // Use setTimeout to ensure DOM is ready
          setTimeout(function () {
            var sortWidget = document.getElementById('recipe-listing-sort');
            var mobileDrawerSortMount = document.getElementById('mobile-drawer-sort-mount');
            if (sortWidget && mobileDrawerSortMount) {
              while (sortWidget.firstChild) {
                mobileDrawerSortMount.appendChild(sortWidget.firstChild);
              }
            }
          }, 0);
        }

        bindMobileDrawerEvents(drawer);
      }

      function closeMobileFilterDrawer() {
        var drawer = document.getElementById('recipe-mobile-filter-drawer');
        if (drawer) {
          // Move sort widget back to its original location before clearing drawer
          var mobileDrawerSortMount = document.getElementById('mobile-drawer-sort-mount');
          var sortWidget = document.getElementById('recipe-listing-sort');
          if (mobileDrawerSortMount && sortWidget) {
            while (mobileDrawerSortMount.firstChild) {
              sortWidget.appendChild(mobileDrawerSortMount.firstChild);
            }
          }

          drawer.classList.remove('is-open');
          drawer.innerHTML = '';
        }
        document.body.classList.remove('no-scroll');

        // Reset panel state to main
        drawerPanel = 'main';
        drawerFacetAttr = null;
      }

      function bindMobileDrawerEvents(drawer) {
        // Close button
        var closeBtn = drawer.querySelector('.algolia-drawer__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', closeMobileFilterDrawer);
        }

        // Overlay click
        var overlay = drawer.querySelector('.algolia-drawer-overlay');
        if (overlay) {
          overlay.addEventListener('click', closeMobileFilterDrawer);
        }

        // Back button (facet panel → main panel)
        var backBtn = drawer.querySelector('.algolia-drawer__back');
        if (backBtn) {
          backBtn.addEventListener('click', function () {
            drawerPanel = 'main';
            drawerFacetAttr = null;

            // Update Clear All Filters button state when navigating back
            var hasRefinements = Object.keys(pendingRefinements).some(function (key) {
              return pendingRefinements[key] && pendingRefinements[key].length > 0;
            });
            // Query button from the current drawer
            var clearBtn = document.querySelector('#recipe-mobile-filter-drawer .algolia-drawer__clear');
            if (clearBtn) {
              clearBtn.disabled = !hasRefinements;
            }

            renderDrawerBody();
          });
        }

        // Facet navigation rows (main panel → facet panel)
        drawer.querySelectorAll('[data-open-facet]').forEach(function (row) {
          row.addEventListener('click', function () {
            drawerPanel = 'facet';
            drawerFacetAttr = row.getAttribute('data-open-facet');
            renderDrawerBody();
          });
        });

        // Facet checkboxes (toggle pending refinements - applied on APPLY click)
        drawer.querySelectorAll('.algolia-filter-checkbox').forEach(function (checkbox) {
          checkbox.addEventListener('change', function () {
            var attr = checkbox.getAttribute('data-attr');
            var value = checkbox.getAttribute('data-value');

            // Initialize array for this attribute if needed
            if (!pendingRefinements[attr]) {
              pendingRefinements[attr] = [];
            }

            if (checkbox.checked) {
              // Add to pending refinements if not already there
              if (pendingRefinements[attr].indexOf(value) === -1) {
                pendingRefinements[attr].push(value);
              }
            } else {
              // Remove from pending refinements
              var idx = pendingRefinements[attr].indexOf(value);
              if (idx > -1) {
                pendingRefinements[attr].splice(idx, 1);
              }
            }

            // Update Clear All Filters button state dynamically
            var hasRefinements = Object.keys(pendingRefinements).some(function (key) {
              return pendingRefinements[key] && pendingRefinements[key].length > 0;
            });

            // Update the mobile drawer Clear All Filters button
            var clearBtn = drawer.querySelector('.algolia-drawer__clear');
            if (clearBtn) {
              clearBtn.disabled = !hasRefinements;
            }
          });
        });

        // Apply button
        var applyBtn = drawer.querySelector('.algolia-drawer__apply');
        if (applyBtn) {
          applyBtn.addEventListener('click', function () {
            // One setUiState call commits every pending facet and the sort as a
            // single search; refining per checkbox would send one request each.
            var indexId = search.indexName;
            search.setUiState(function (uiState) {
              var indexState = Object.assign({}, uiState[indexId]);
              var refinementList = Object.assign({}, indexState.refinementList);
              Object.keys(pendingRefinements).forEach(function (attr) {
                if (pendingRefinements[attr].length) refinementList[attr] = pendingRefinements[attr].slice();
                else delete refinementList[attr];
              });
              indexState.refinementList = refinementList;
              if (pendingSortIndexName !== null) indexState.sortBy = pendingSortIndexName;
              delete indexState.page;
              var next = Object.assign({}, uiState);
              next[indexId] = indexState;
              return next;
            });

            // Clear pending refinements
            pendingRefinements = {};

            closeMobileFilterDrawer();
          });
        }

        // Clear filters button (matches search page implementation)
        var clearBtn = drawer.querySelector('.algolia-drawer__clear');
        if (clearBtn) {
          // Enable/disable based on pending refinements
          var hasRefinements = Object.keys(pendingRefinements).some(function (key) {
            return pendingRefinements[key] && pendingRefinements[key].length > 0;
          });
          clearBtn.disabled = !hasRefinements;

          clearBtn.addEventListener('click', function () {
            // Clear all filters and pending refinements
            search.helper.clearRefinements().search();
            pendingRefinements = {};

            // Close drawer and show results immediately (matching search page behavior)
            closeMobileFilterDrawer();
          });
        }
      }

      // Bind mobile filter button clicks
      document.addEventListener('click', function (e) {
        var filterBtn = e.target.closest('.algolia-mobile-filter-button');
        if (filterBtn) {
          e.preventDefault();
          openMobileFilterDrawer();
        }
      });

      // Wire up the "Clear filters" CTA inside the empty-state template above.
      document.addEventListener('click', function (e) {
        if (e.target && e.target.matches('[data-clear-refinements-trigger]')) {
          search.helper.clearRefinements().search();
        }
      });
    } // End of continueInitialization
  } // End of init()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
